import { useRef, useState } from 'react'
import CloseIconButton, { DARK_APPEARANCE } from '../../../components/CloseIconButton'
import TemplateRow from './TemplateRow'
import { isDeletable, DRAFT_TEMPLATE_ID } from './templateFields'
import { TEMPLATE_COLUMNS } from './templateColumns'

// Letter 122 item 5, Figma `delete-hover` (05.10): "You can’t delete your only {type} template. You
// can still edit it." {type} is the template's own type, lowercase as its sibling save-errors copy
// reads ("This loan scenario ...") -- README.md ("Blocked delete message").
export function blockedDeleteMessage(templateType) {
  const type = typeof templateType === 'string' ? templateType.trim().toLowerCase() : ''
  return `You can\u2019t delete your only ${type ? `${type} ` : ''}template. You can still edit it.`
}

function formatLastModified(value) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
}

// Manage Templates' pill, placed and drawn as #btn_49 (30.09 export): 32 tall, rx16, 1px #DAE4D5,
// 14px circle 12 in from the left with a drawn #4CBC18 plus (6px arms), label 2 after it, 12 right
// padding (11 + the 1px border, which Figma draws inside); hover turns border, plus and label #328C12
// (#buttons-hover). Letter 106 R4d / 01.10 export: the label reads Save as New Template, 178 wide as
// drawn (the label alone measures ~173 in the browser).
function ManageNewTemplateButton({ onClick, disabled, disabledReason }) {
  const [hovered, setHovered] = useState(false)
  const accent = hovered && !disabled ? '#328C12' : null
  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={disabledReason}
        aria-describedby={disabledReason ? 'save-as-new-reason' : undefined}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="self-start w-[178px] h-[32px] rounded-[16px] pl-[11px] pr-[11px] flex items-center gap-x-[2px] disabled:opacity-60"
        style={{ border: `1px solid ${accent ?? '#DAE4D5'}` }}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <circle cx="7" cy="7" r="7" fill="#F4FAEF" />
          <path d="M7 4V10M10 7H4" stroke={accent ?? '#4CBC18'} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-[13px] font-semibold" style={{ color: accent ?? '#17270D' }}>
          Save as New Template
        </span>
      </button>
      {disabledReason && (
        <span id="save-as-new-reason" className="sr-only">
          {disabledReason}
        </span>
      )}
    </>
  )
}

// Manage Templates: the list state and the rename state (S8) are one panel, switched by `view`, so the
// row list, its floor-gated delete and its reorder controls are never duplicated. "Start your
// analysis" is its own pop-up (StartAnalysisModal.jsx).
export default function TemplateListModal({
  templates,
  checkedIds,
  onToggleChecked,
  onSave,
  canSave,
  saveError,
  view,
  editingId,
  editValue,
  onEditValueChange,
  onStartEdit,
  onCancelEdit,
  onCommitEdit,
  onCreateNew,
  onRequestDelete,
  onMoveUp,
  onMoveDown,
  onClose,
  busy,
  error,
}) {
  const backdropRef = useRef(null)
  const radioGroupRef = useRef(null)
  const checkedCount = checkedIds.size
  const updateDisabledReason = !canSave
    ? 'Open a saved scenario to update a template with it.'
    : checkedCount !== 1
      ? 'Select a template to update.'
      : null

  // Manage's radiogroup: the selected radio (or the first, with none) is the one Tab stop; an arrow
  // selects the next or previous row and moves focus to it.
  const tabbableIndex = Math.max(0, templates.findIndex((t) => checkedIds.has(t._id)))
  const handleRadioArrow = (index, step) => {
    const nextIndex = (index + step + templates.length) % templates.length
    if (!templates[nextIndex]) return
    onToggleChecked(templates[nextIndex]._id)
    radioGroupRef.current?.querySelectorAll('[role="radio"]')[nextIndex]?.focus()
  }

  const draftRow = editingId === DRAFT_TEMPLATE_ID && (
    <TemplateRow
      draft
      template={{ _id: DRAFT_TEMPLATE_ID, template_name: '' }}
      isEditing
      editValue={editValue}
      onEditValueChange={onEditValueChange}
      onCommitEdit={onCommitEdit}
      onCancelEdit={onCancelEdit}
    />
  )

  const renderRows = () =>
    templates.map((template, index) => (
      <TemplateRow
        radioTabbable={index === tabbableIndex}
        onRadioArrow={(step) => handleRadioArrow(index, step)}
        key={template._id}
        template={template}
        checked={checkedIds.has(template._id)}
        onToggleChecked={() => onToggleChecked(template._id)}
        isEditing={editingId === template._id}
        editValue={editValue}
        onEditValueChange={onEditValueChange}
        onCommitEdit={onCommitEdit}
        onCancelEdit={onCancelEdit}
        onEdit={() => onStartEdit(template)}
        onDelete={() => onRequestDelete(template)}
        deletable={isDeletable(templates, template)}
        deleteTooltip={blockedDeleteMessage(template.template_type)}
        onMoveUp={() => onMoveUp(template._id)}
        onMoveDown={() => onMoveDown(template._id)}
        canMoveUp={index > 0}
        canMoveDown={index < templates.length - 1}
        lastModifiedLabel={formatLastModified(template.modified)}
      />
    ))

  const emptyMessage = (
    // No frame draws this -- an empty list is the real starting state for every account that
    // predates starter-template seeding (reilm-be/modules/users/README.md), so it needs a
    // functional message, not a blank panel under the header.
    <p className="py-[24px] text-[13px] text-brand-unselectedGray text-center">
      No templates yet. Use Save as New Template above to save a scenario as one.
    </p>
  )

  const renameButtons = (
    <div className="flex items-center gap-x-[12px]">
      <button
        type="button"
        onClick={onCancelEdit}
        // The reference "buttons-hover" board.
        className="w-[96px] h-[40px] rounded-[20px] border border-brand-fieldBdr text-brand-dark text-[13px] font-semibold hover:border-brand-hover hover:text-brand-hover"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onCommitEdit}
        // Letter 106 R1: Save has no disabled state; an empty name gets the pop-up's error line
        // (TemplatesFlow.handleCommitEdit) and a click mid-request is ignored there.
        className="w-[96px] h-[40px] rounded-[20px] bg-[#4AA52A] hover:bg-[#328C12] text-white text-[13px] font-semibold"
      >
        Save
      </button>
    </div>
  )

  // Opened from the header's Templates button (letter 99, item 2; letter 106 R4; 01.10 export
  // #templates-pop-up): 48 top/bottom, 40 sides, pill 24 under the heading and 16 over the table,
  // footer 48 under the table. One radio per row, no Import.
  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[150] bg-black/50 flex items-center justify-center"
      // Same backdrop-close convention as the branch below (mousedown + target identity).
      onMouseDown={(e) => {
        if (e.target === backdropRef.current) onClose()
      }}
    >
      <div className="relative w-[486px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-32px)] rounded-[16px] bg-[#F4FAEF] flex flex-col">
        <div className="px-[16px] sm:px-[40px] pt-[48px] flex flex-col">
          <h2 className="text-[20px] leading-[24px] font-extrabold text-brand-dark">Manage Templates</h2>
          {/* 16px glyph, 1px stroke, 20 from the top and right edges (#close_5): the 24px button
              box is centred on the glyph, hence 16. */}
          <CloseIconButton
            onClick={onClose}
            ariaLabel="Close"
            appearance={DARK_APPEARANCE}
            span={16}
            stroke={1}
            className="absolute top-[16px] right-[16px]"
          />
          <div className="mt-[24px] flex">
            <ManageNewTemplateButton
              onClick={onCreateNew}
              disabled={busy || !canSave || view === 'rename'}
              disabledReason={!canSave ? 'Open a saved scenario to save it as a new template.' : undefined}
            />
          </div>
          {error && <p className="mt-[8px] text-[12px] text-red-500">{error}</p>}
        </div>

        {/* Header row 24 tall, its rule inside the 40px insets so it stops at the table's edge
            (2.4.1); 12/600 on a 20px line puts the baseline 14.2 down, as drawn. */}
        <div className="mx-[16px] sm:mx-[40px] mt-[16px] h-[24px] flex items-start border-b border-brand-fieldBdr">
          <span className="flex-1 pl-[26px] text-[12px] leading-[20px] font-semibold text-brand-dark">Name</span>
          <span className={`${TEMPLATE_COLUMNS.manageDate} shrink-0 text-right text-[12px] leading-[20px] font-semibold text-brand-dark`}>
            Last Modified
          </span>
          <span className={`${TEMPLATE_COLUMNS.manageIconGap} ${TEMPLATE_COLUMNS.manageIcons} shrink-0`} />
        </div>

        <div ref={radioGroupRef} role="radiogroup" aria-label="Templates" className="px-[16px] sm:px-[40px] mt-[4px] overflow-y-auto flex-1">
          {templates.length === 0 && !draftRow && emptyMessage}
          {renderRows()}
          {draftRow}
        </div>

        <div className="px-[16px] sm:px-[40px] pt-[48px] pb-[48px] flex flex-col items-end">
          {/* save-errors board: the pill sits above the button, in flow. Its drawn right edge (Save's
              + 67) belonged to a footer with Import to the right; Update Template is the rightmost
              control, so the pill keeps its right edge to stay inside the panel. */}
          {view !== 'rename' && saveError && (
            <p
              id="template-save-error"
              role="alert"
              className="mb-[8px] w-max max-w-full rounded-[8px] bg-white px-[10px] py-[7px] text-[13px] leading-[16px] text-brand-statusSale shadow-[0_0_10px_rgba(0,0,0,0.1)]"
            >
              {saveError}
            </p>
          )}
          {view === 'rename' ? (
            renameButtons
          ) : (
            // Update Template (letter 106 R4b; 01.10 export, 150x40 rx20, 13/600 white): disabled
            // #A9B9A3 until one template is selected (R4b.i) and while no saved scenario is open;
            // enabled #4AA52A, hover #328C12. Overwrites the selected template after the
            // Overwrite confirmation (TemplatesFlow.handleRequestSave).
            <span>
              <button
                type="button"
                onClick={onSave}
                // Not `busy`: a reorder or rename in flight would flash it grey; the handler ignores a
                // click mid-request.
                disabled={Boolean(updateDisabledReason)}
                title={updateDisabledReason ?? undefined}
                aria-describedby={saveError ? 'template-save-error' : updateDisabledReason ? 'template-update-reason' : undefined}
                className="w-[150px] h-[40px] rounded-[20px] bg-[#4AA52A] enabled:hover:bg-[#328C12] disabled:bg-[#A9B9A3] disabled:cursor-not-allowed text-white text-[13px] font-semibold"
              >
                Update Template
              </button>
              {updateDisabledReason && (
                <span id="template-update-reason" className="sr-only">
                  {updateDisabledReason}
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
